import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, Camera, CheckCircle2, FileText, Loader2, RotateCcw, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { FadeUp } from "@/components/landing/Reveal";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { api, fileUrl, formatApiError } from "@/lib/api";

export const PRIORITY = {
  "Crítica": { text: "text-rose-400", border: "border-rose-500/40", bg: "bg-rose-500/10", bar: "#F43F5E" },
  Alta: { text: "text-amber-400", border: "border-amber-500/40", bg: "bg-amber-500/10", bar: "#F59E0B" },
  Media: { text: "text-sky-300", border: "border-sky-400/40", bg: "bg-sky-400/10", bar: "#38BDF8" },
};

export const ResolveControl = ({ auditId, it, onAudit }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn, ok) => {
    setBusy(true);
    try { onAudit((await fn()).data); toast.success(ok); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const isDoc = it.kind === "documental";
  const onFile = (file) => {
    const okType = file?.type?.startsWith("image/") || (isDoc && file?.type === "application/pdf");
    if (!okType) return toast.error(isDoc ? "Selecciona un PDF o una imagen" : "Selecciona una imagen");
    run(async () => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(isDoc ? "/uploads?kind=document" : "/uploads", fd);
      return api.post(`/audits/${auditId}/resolutions`, { question_id: it.question_id, photo_path: data.path });
    }, "Hallazgo marcado como resuelto");
  };

  if (it.resolved) {
    return (
      <div className="flex items-center gap-3">
        <a href={fileUrl(it.resolution_photo)} target="_blank" rel="noreferrer" className="grid place-items-center w-14 h-14 overflow-hidden border border-emerald-500/40" data-testid={`resolution-photo-${it.question_id}`}>
          {it.resolution_photo?.toLowerCase().endsWith(".pdf") ? <FileText className="w-6 h-6 text-emerald-300" /> : <img src={fileUrl(it.resolution_photo)} alt="Evidencia de cierre" className="w-full h-full object-cover" />}
        </a>
        {onAudit && (
          <button type="button" disabled={busy} onClick={() => run(() => api.delete(`/audits/${auditId}/resolutions/${it.question_id}`), "Hallazgo reabierto")} data-testid={`action-reopen-${it.question_id}`}
            className="inline-flex items-center gap-1.5 font-mono text-xs text-[#F5F1E8]/65 hover:text-[#F59E0B] transition-colors">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Reabrir
          </button>
        )}
      </div>
    );
  }
  if (!onAudit) return null;
  return (
    <>
      <motion.button type="button" whileTap={{ scale: 0.95 }} disabled={busy} onClick={() => ref.current?.click()} data-testid={`action-resolve-${it.question_id}`}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/50 text-emerald-300 text-xs font-semibold hover:bg-emerald-500 hover:text-[#0B0F17] transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isDoc ? <Upload className="w-3.5 h-3.5" /> : <Camera className="w-3.5 h-3.5" />} {isDoc ? "Subir documento de cierre" : "Marcar resuelta"}
      </motion.button>
      <input ref={ref} type="file" accept={isDoc ? "application/pdf,image/*" : "image/*"} {...(isDoc ? {} : { capture: "environment" })} className="hidden" data-testid={`action-resolve-input-${it.question_id}`} onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
    </>
  );
};

export const ActionPlan = ({ items = [], auditId, onAudit, whatsappMessage }) => {
  const done = items.filter((i) => i.resolved).length;
  return (
  <FadeUp className="mt-16" data-testid="action-plan-section">
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">Plan de acción</div>
        <h2 className="font-display mt-3 text-2xl sm:text-3xl font-bold">Qué corregir primero</h2>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-4">
          <p className="text-sm text-[#F5F1E8]/70" data-testid="action-plan-count"><span className="text-emerald-400 font-semibold">{done}</span> de {items.length} hallazgos solucionados</p>
          {whatsappMessage && <WhatsAppButton message={whatsappMessage} testid="action-plan-whatsapp-button" compact />}
        </div>
      )}
    </div>
    {items.length > 0 && (
      <div className="mt-5 h-1.5 bg-white/5" data-testid="action-plan-progress">
        <motion.div className="h-full bg-emerald-400" animate={{ width: `${(done / items.length) * 100}%` }} transition={{ duration: 0.8 }} />
      </div>
    )}
    {items.length === 0 ? (
      <div className="mt-6 border border-emerald-500/30 bg-emerald-500/5 p-8 flex items-center gap-4 text-emerald-300" data-testid="action-plan-empty">
        <ShieldCheck className="w-6 h-6 shrink-0" /> Sin incumplimientos: no hay acciones correctivas pendientes.
      </div>
    ) : (
      <ol className="mt-6 border-t border-white/10">
        {items.map((it, i) => {
          const p = it.resolved ? { ...PRIORITY.Media, bar: "#10B981" } : PRIORITY[it.priority] ?? PRIORITY.Media;
          return (
            <motion.li key={it.question_id} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ delay: i * 0.05, duration: 0.5 }}
              className="relative grid grid-cols-12 gap-4 py-6 border-b border-white/10 pl-4" data-testid={`action-item-${it.question_id}`}>
              <span className="absolute left-0 top-6 bottom-6 w-[3px]" style={{ background: p.bar }} />
              <span className="col-span-2 sm:col-span-1 font-display-x text-2xl font-black text-[#F5F1E8]/30">{String(i + 1).padStart(2, "0")}</span>
              <div className="col-span-10 sm:col-span-8 min-w-0">
                <p className={`text-base sm:text-lg font-semibold leading-snug ${it.resolved ? "text-[#F5F1E8]/55 line-through decoration-emerald-400/60" : ""}`}>{it.action}</p>
                <p className="mt-2 text-sm text-[#F5F1E8]/60"><span className="font-mono text-[#F5F1E8]/50 mr-2">{it.question_id.toUpperCase()} · {it.code}</span>{it.question}</p>
              </div>
              <div className="col-span-10 col-start-3 sm:col-span-3 sm:col-start-auto flex sm:flex-col sm:items-end flex-wrap items-center gap-2">
                {it.resolved ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold bg-emerald-500/10 border-emerald-500/40 text-emerald-400" data-testid={`action-priority-${it.question_id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Resuelta</span>
                ) : (
                  <span className={`px-3 py-1 rounded-full border text-xs font-semibold ${p.bg} ${p.border} ${p.text}`} data-testid={`action-priority-${it.question_id}`}>{it.priority}</span>
                )}
                {it.resolved ? (
                  <span className="font-mono text-xs text-[#F5F1E8]/60">{new Date(it.resolved_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short" })}</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs text-[#F5F1E8]/70"><CalendarClock className="w-3.5 h-3.5" /> ≤ {it.days} días</span>
                )}
                <ResolveControl auditId={auditId} it={it} onAudit={onAudit} />
              </div>
            </motion.li>
          );
        })}
      </ol>
    )}
  </FadeUp>
  );
};
